import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import * as brandApi from '../../api/brandApi';

interface ProtectedBrandRouteProps {
  children: React.ReactNode;
  requiredRole?: 'owner' | 'admin' | 'editor' | 'viewer';
}

const ProtectedBrandRoute: React.FC<ProtectedBrandRouteProps> = ({
  children,
  requiredRole = 'viewer',
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { brands, loading: brandsLoading } = useBrand();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkAccess();
  }, [isAuthenticated, brands, slug, user]);

  const checkAccess = async () => {
    try {
      setChecking(true);

      // Not authenticated - no access
      if (!isAuthenticated) {
        setHasAccess(false);
        setChecking(false);
        return;
      }

      // SuperAdmin - always has access
      if (user?.is_super_admin) {
        setHasAccess(true);
        setChecking(false);
        return;
      }

      // Find brand by slug
      const brand = brands.find((b) => b.slug === slug);

      if (!brand) {
        // Brand not found in user's accessible brands
        setHasAccess(false);
        setChecking(false);
        return;
      }

      // Check role hierarchy
      const roleHierarchy = {
        owner: 4,
        admin: 3,
        editor: 2,
        viewer: 1,
      };

      const userRoleLevel = roleHierarchy[brand.role];
      const requiredRoleLevel = roleHierarchy[requiredRole];

      const hasPermission = userRoleLevel >= requiredRoleLevel;
      setHasAccess(hasPermission);
    } catch (err) {
      console.error('Error checking brand access:', err);
      setHasAccess(false);
    } finally {
      setChecking(false);
    }
  };

  // Show loading state - Wait for AuthContext to finish loading
  if (authLoading || checking || brandsLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Checking access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - land on the homepage with the sign-in sheet open,
  // preserving the target URL so the sheet can bounce back here after
  // successful login (the standalone /login page remains a manual backup).
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/?signin=1&returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // No access - redirect to home
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">
            You don't have permission to access this brand.
          </p>
          <Navigate to="/" replace />
        </div>
      </div>
    );
  }

  // Has access - render children
  return <>{children}</>;
};

export default ProtectedBrandRoute;
